import { useMutation, useQuery } from "@tanstack/react-query";
import { Deal } from "src/business/deal";
import { API_URL } from "src/Utils/API";

const getRestaurantDealsQueryKey = (restaurantId: string, startDate?: number, endDate?: number) =>
    ["restaurants", restaurantId, "deals", { startDate, endDate }];

export const useGetRestaurantDeals = ({
    restaurantId,
    startDate,
    endDate,
}: {
    restaurantId?: string;
    startDate?: number;
    endDate?: number;
}) => {
    return useQuery({
        queryKey: getRestaurantDealsQueryKey(restaurantId ?? "", startDate, endDate),
        queryFn: async (): Promise<Deal[]> => {
            const params = new URLSearchParams();
            if (startDate !== undefined) params.append("startDate", String(startDate));
            if (endDate !== undefined) params.append("endDate", String(endDate));
            const query = params.toString() ? `?${params.toString()}` : "";
            const response = await fetch(
                `${API_URL}/restaurants/${restaurantId}/deals${query}`,
                { credentials: "include" }
            );
            const data = await response.json();
            if (data.success) {
                return data.data;
            }
            throw new Error(data.error || "Failed to fetch deals");
        },
        enabled: !!restaurantId,
        // Keep the deal list cached so the table schedule can resolve deals for
        // any date without re-hitting the endpoint as the user scrolls dates.
        staleTime: 5 * 60 * 1000,
    });
};

export const useAddRestaurantDeal = (onSuccess?: () => void) => {
    return useMutation({
        mutationFn: async ({
            restaurantId,
            payload,
        }: {
            restaurantId: string;
            payload: Omit<Deal, 'id'>;
        }) => {
            const response = await fetch(
                `${API_URL}/restaurants/${restaurantId}/deals`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                }
            );
            const data = await response.json();
            if (!data.success) {
                throw new Error(data.error || "Failed to add deal");
            }
        },
        onSuccess,
    });
};

export const useUpdateRestaurantDeal = (onSuccess?: () => void) => {
    return useMutation({
        mutationFn: async ({
            restaurantId,
            dealId,
            payload,
        }: {
            restaurantId: string;
            dealId: string;
            payload: Omit<Deal, 'id'>;
        }) => {
            const response = await fetch(
                `${API_URL}/restaurants/${restaurantId}/deals/${dealId}`,
                {
                    method: "PUT",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                }
            );
            const data = await response.json();
            if (!data.success) {
                throw new Error(data.error || "Failed to update deal");
            }
        },
        onSuccess,
    });
};
